import { Badge } from "@sylviq/ui-react-web/components/ui/badge";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { Drawer } from "@sylviq/ui-react-web/components/ui/drawer";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@sylviq/ui-react-web/components/ui/table";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { closeFiscalYear, createFiscalYear, type FiscalYear, listFiscalYears, reopenFiscalYear } from "@/lib/api";
function toErrorMessage(cause: unknown): string { return cause instanceof Error ? cause.message : String(cause); }
export function FiscalYearsSettings() {
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [years, setYears] = useState<FiscalYear[] | null>(null);
  const [yearsLoading, setYearsLoading] = useState(true);
  const [yearsError, setYearsError] = useState<string | null>(null);
  const [yearName, setYearName] = useState("");
  const [yearStart, setYearStart] = useState("");
  const [yearEnd, setYearEnd] = useState("");
  const [creating, setCreating] = useState(false);
  const [actingYear, setActingYear] = useState<string | null>(null);
  const [pendingCloseYear, setPendingCloseYear] = useState<FiscalYear | null>(
    null,
  );

  const reloadYears = useCallback(() => {
    setYearsLoading(true);
    setYearsError(null);
    listFiscalYears()
      .then(setYears)
      .catch((cause: unknown) => setYearsError(toErrorMessage(cause)))
      .finally(() => setYearsLoading(false));
  }, []);

  useEffect(() => { reloadYears(); }, [reloadYears]);
  const submitYear = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setCreating(true);
    setError(null);
    setNotice(null);
    try {
      if (!yearName.trim()) throw new Error("Fiscal year name is required.");
      if (!yearStart || !yearEnd)
        throw new Error("Start and end dates are both required.");
      if (yearEnd < yearStart)
        throw new Error("End date must be on or after the start date.");
      await createFiscalYear({
        name: yearName.trim(),
        start_date: yearStart,
        end_date: yearEnd,
      });
      setEditorOpen(false);
      setYearName("");
      setYearStart("");
      setYearEnd("");
      reloadYears();
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setCreating(false);
    }
  };

  const requestCloseYear = (year: FiscalYear) => {
    setPendingCloseYear(year);
  };

  const confirmCloseYear = async () => {
    if (!pendingCloseYear) return;
    const year = pendingCloseYear;
    setActingYear(year.id);
    setError(null);
    setNotice(null);
    try {
      const result = await closeFiscalYear(year.id);
      setNotice(
        `Fiscal year "${year.name}" closed.` +
          (result.closing_entry_id
            ? ` Closing entry: ${result.closing_entry_id}.`
            : " No closing entry was needed (quiet period)."),
      );
      reloadYears();
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setActingYear(null);
      setPendingCloseYear(null);
    }
  };

  const submitReopen = async (year: FiscalYear) => {
    setActingYear(year.id);
    setError(null);
    setNotice(null);
    try {
      await reopenFiscalYear(year.id);
      setNotice(`Fiscal year "${year.name}" reopened.`);
      reloadYears();
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setActingYear(null);
    }
  };


  return (
    <section aria-label="Fiscal years" className="grid gap-4">
      <p className="text-sm text-[var(--muted-foreground)]">Fiscal years define reporting periods for these books. Year-end closing posts a closing entry and locks the period; it is separate from setting an ordinary period lock.</p>
      <div className="actions-row"><Button type="button" disabled={creating || actingYear !== null} onClick={() => setEditorOpen(true)}>New fiscal year</Button></div>
      {error && !editorOpen ? <p role="alert" className="text-sm text-[var(--danger)]">{error}</p> : null}
      {notice ? <output className="text-sm text-[var(--success)]">{notice}</output> : null}
          <Drawer open={editorOpen} onOpenChange={next => { if (!creating) setEditorOpen(next); }} title="New fiscal year"
            description="Define the reporting year's name and date range. Creating it does not close or lock the books."
            footer={<><Button type="button" variant="secondary" disabled={creating} onClick={() => setEditorOpen(false)}>Cancel</Button><Button type="submit" form="fiscal-year-form" disabled={creating}>{creating ? "Creating..." : "Create year"}</Button></>}>
          {error ? <p role="alert" className="mb-3 text-sm text-[var(--danger)]">{error}</p> : null}
          <form id="fiscal-year-form"
            onSubmit={submitYear}
            className="grid gap-3"
          >
            <label htmlFor="fy-name" className="grid gap-1 text-xs font-medium">
              Name
              <Input
                id="fy-name"
                value={yearName}
                onChange={(e) => setYearName(e.target.value)}
                required
                placeholder="FY 2026"
                disabled={creating}
              />
            </label>
            <label htmlFor="fy-start" className="grid gap-1 text-xs font-medium">
              Start date
              <Input
                id="fy-start"
                type="date"
                value={yearStart}
                onChange={(e) => setYearStart(e.target.value)}
                required
                disabled={creating}
              />
            </label>
            <label htmlFor="fy-end" className="grid gap-1 text-xs font-medium">
              End date
              <Input
                id="fy-end"
                type="date"
                value={yearEnd}
                onChange={(e) => setYearEnd(e.target.value)}
                required
                disabled={creating}
              />
            </label>
          </form>
          </Drawer>

          {yearsError ? (
            <div
              role="alert"
              className="surface grid gap-3 rounded-2xl p-5 text-sm text-[var(--danger)]"
            >
              <p>Could not load fiscal years: {yearsError}</p>
              <div>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={reloadYears}
                  disabled={yearsLoading}
                >
                  {yearsLoading ? "Retrying..." : "Retry"}
                </Button>
              </div>
            </div>
          ) : null}

          {years === null && yearsError ? null : (
            <div className="surface overflow-hidden rounded-2xl">
              <div className="table-scroll">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Start</TableHead>
                      <TableHead>End</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {yearsLoading && years === null ? (
                      <TableRow>
                        <TableCell colSpan={5}>Loading…</TableCell>
                      </TableRow>
                    ) : (years ?? []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5}>
                          No fiscal years yet — choose New fiscal year.
                        </TableCell>
                      </TableRow>
                    ) : (
                      (years ?? []).map((year) => (
                        <TableRow key={year.id}>
                          <TableCell>{year.name}</TableCell>
                          <TableCell className="whitespace-nowrap">
                            {String(year.start_date).slice(0, 10)}
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            {String(year.end_date).slice(0, 10)}
                          </TableCell>
                          <TableCell>
                            <Badge tone={year.is_closed ? "neutral" : "success"}>
                              {year.is_closed ? "Closed" : "Open"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {year.is_closed ? (
                              <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                onClick={() => submitReopen(year)}
                                disabled={actingYear === year.id}
                              >
                                {actingYear === year.id ? "Reopening..." : "Reopen"}
                              </Button>
                            ) : (
                              <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                onClick={() => requestCloseYear(year)}
                                disabled={actingYear === year.id}
                              >
                                {actingYear === year.id ? "Closing..." : "Close"}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          <Dialog
            open={pendingCloseYear !== null}
            onOpenChange={(open) => {
              if (!open && actingYear === null) setPendingCloseYear(null);
            }}
            title="Close fiscal year?"
            description={
              pendingCloseYear
                ? `Close fiscal year "${pendingCloseYear.name}" (${pendingCloseYear.start_date} → ${pendingCloseYear.end_date})? ` +
                  "This posts the year-end closing entry, marks the year closed, and " +
                  "locks the period — postings into it will be rejected with a 409. " +
                  "This is destructive; reopen is possible but voids the closing entry."
                : undefined
            }
          >
            <div className="actions-row mt-4 flex-wrap">
              <Button
                type="button"
                variant="secondary"
                disabled={actingYear !== null}
                onClick={() => setPendingCloseYear(null)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={actingYear !== null}
                onClick={() => void confirmCloseYear()}
              >
                {actingYear !== null ? "Closing..." : "Close fiscal year"}
              </Button>
            </div>
          </Dialog>
    </section>
  );
}
